"use client";

import { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/system";
import baseURL from "../../config/config";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import BookIcon from '@mui/icons-material/Book';
import axiosInstance from "../../helpers/Axios";

// STYLED COMPONENTS (No changes needed here)
const StyledBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 30vh;
  padding: 2rem;
  background-color: #f3f4f6;
  margin-top: 5rem;
`;

const FormContainer = styled(Box)`
  background-color: white;
  padding: 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 28rem;
`;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 0.75rem;
    margin: 1rem;
  }
`;

const ResultCard = styled(Box)`
  background-color: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  background-color: #2563eb;
  &:hover {
    background-color: #1d4ed8;
  }
`;

const UploadWordMeaning = () => {
  const [csvFile, setCsvFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [open, setOpen] = useState(false);

  const handleFileChange = (event) => {
    setCsvFile(event.target.files[0]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!csvFile) {
      alert("Please upload a CSV file.");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("csv_file", csvFile);

    try {
      const response = await axiosInstance.post(
        `${baseURL}/api/trail/import-meanings-csv/`,
        formData
      );
      // The response has a nested structure: { data: { data: { ... } } }
      // We store the first 'data' object.
      setResponseData(response.data);
    } catch (error) {
      console.error("Error uploading file:", error);
      setResponseData({
        status: "error",
        data: { // Ensure 'data' object exists for error handling
          message:
            error.response?.data?.error ||
            error.response?.data?.detail ||
            "Upload failed! Please try again.",
        },
      });
    } finally {
      setLoading(false);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Reset state for next upload
    const form = document.querySelector('form');
    if (form) {
      form.reset();
    }
    setCsvFile(null);
    setResponseData(null);
  };

  const renderUploadResults = () => {
    // Correctly access the nested results array
    const results = responseData?.data?.data?.results;

    if (!results || results.length === 0) {
      return null;
    }
    
    // Helper function to parse each result string into an object
    const parseResultString = (resultStr) => {
      const wordMatch = resultStr.match(/\('([^']+)'\)/);
      const word = wordMatch ? wordMatch[1] : "Unknown";
      let status = "info"; // Default status

      if (resultStr.toLowerCase().includes("skipped")) status = "skipped";
      if (resultStr.toLowerCase().includes("created")) status = "created";
      if (resultStr.toLowerCase().includes("updated")) status = "updated";
      if (resultStr.toLowerCase().includes("error")) status = "error";

      return { word, status, message: resultStr };
    };

    return (
      <div className="mt-6 border border-gray-200 p-3 rounded-lg">
        <Typography variant="h6" className="mb-3 text-gray-800 font-medium">
          Upload Details ({results.length})
        </Typography>
        <div className="max-h-80 overflow-y-auto pr-1">
          {results.map((resultString, index) => {
            const result = parseResultString(resultString);
            const isSuccess = result.status === "created" || result.status === "updated";
            const isSkipped = result.status === "skipped";
            
            // Define color schemes for different statuses
            const colorScheme = isSuccess
              ? { bg: "bg-green-50", border: "border-green-200", text: "text-green-700", badge: "bg-green-100 text-green-800", icon: <CheckCircleIcon/> }
              : isSkipped
              ? { bg: "bg-yellow-50", border: "border-yellow-200", text: "text-yellow-700", badge: "bg-yellow-100 text-yellow-800", icon: <ErrorIcon/> }
              : { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", badge: "bg-red-100 text-red-800", icon: <ErrorIcon/> };

            return (
              <div key={index} className={`${colorScheme.bg} p-3 rounded-lg mb-2 border ${colorScheme.border}`}>
                <div className="flex items-center">
                  <p className={`font-semibold text-sm ${colorScheme.text} flex-grow`}>
                    Word: {result.word}
                  </p>
                  <p className={`text-xs font-medium ${colorScheme.badge} px-2 py-1 rounded`}>
                    {result.status.toUpperCase()}
                  </p>
                </div>
                <div className="bg-white p-2 rounded border-l-2 ${colorScheme.border} mt-2">
                   <p className="text-sm text-gray-600">{result.message}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };


  return (
    <StyledBox>
      <FormContainer>
        <Typography variant="h4" className="text-center mb-6 font-semibold text-gray-800">
          Bulk Meaning Upload
        </Typography>
        <form onSubmit={handleSubmit} className="space-y-6">
          <TextField
            type="file"
            label="CSV File"
            accept=".csv"
            onChange={handleFileChange}
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <DescriptionIcon className="w-5 h-5 text-gray-400 mr-2" />
              ),
            }}
          />
          <StyledButton type="submit" variant="contained" fullWidth className="py-2.5" disabled={loading}>
            {loading ? <CircularProgress size={24} color="inherit" /> : (
              <><BookIcon className="w-5 h-5 mr-2" /> Upload Meanings</>
            )}
          </StyledButton>
        </form>
      </FormContainer>

      <StyledDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <div className="relative">
          <div className="px-6 py-4 border-b">
            <div className="flex justify-between items-center">
              <Typography variant="h6" className="text-gray-800">Bulk Upload Results</Typography>
              <IconButton onClick={handleClose} className="text-gray-400 hover:text-gray-600">
                <CloseIcon />
              </IconButton>
            </div>
          </div>
          <DialogContent className="px-6 py-5">
            {responseData && (
              <div className="space-y-4">
                <div className={`flex items-center space-x-3 p-4 rounded-lg ${responseData.status === "success" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                  {responseData.status === "success" ? <CheckCircleIcon className="h-5 w-5 text-green-500" /> : <ErrorIcon className="h-5 w-5 text-red-500" />}
                  {/* Access the innermost message */}
                  <p className="font-medium">{responseData.data?.data?.message || responseData.data?.message}</p>
                </div>
                {responseData.status === "success" && responseData.data?.data && (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <ResultCard>
                      <Typography variant="subtitle2" className="text-gray-500">Total Processed</Typography>
                      {/* Access nested 'total_processed' */}
                      <Typography variant="h6" className="text-gray-900">{responseData.data.data.total_processed}</Typography>
                    </ResultCard>
                    <ResultCard>
                      <Typography variant="subtitle2" className="text-gray-500">Created/Updated</Typography>
                      {/* Access nested 'mappings_created' */}
                      <Typography variant="h6" className="text-green-600">{responseData.data.data.mappings_created}</Typography>
                    </ResultCard>
                    <ResultCard>
                      <Typography variant="subtitle2" className="text-gray-500">Skipped/Failed</Typography>
                      {/* Calculate failed rows since it's not in the API response */}
                      <Typography variant="h6" className="text-red-600">
                        {responseData.data.data.total_processed - responseData.data.data.mappings_created}
                      </Typography>
                    </ResultCard>
                    <ResultCard>
                      <Typography variant="subtitle2" className="text-gray-500">Success Rate</Typography>
                      {/* Use correct nested paths for calculation */}
                      <Typography variant="h6" className="text-blue-600">
                        {responseData.data.data.total_processed > 0
                          ? Math.round((responseData.data.data.mappings_created / responseData.data.data.total_processed) * 100)
                          : 0}%
                      </Typography>
                    </ResultCard>
                  </div>
                )}
                {renderUploadResults()}
              </div>
            )}
          </DialogContent>
          <div className="border-t px-6 py-4 bg-gray-50">
            <div className="flex justify-end">
              <StyledButton onClick={handleClose} variant="contained">Close</StyledButton>
            </div>
          </div>
        </div>
      </StyledDialog>
    </StyledBox>
  );
};

export default UploadWordMeaning;
